import { useMoney } from '../hooks/useMoney';
import { useEffect, useState, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  Banner,
  TextField,
  DataTable,
  Spinner,
  Select,
  Thumbnail,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';

interface BestSellerItem {
  product_id: number;
  sku: string;
  title: string;
  vendor: string | null;
  featured_image_url: string | null;
  total_quantity: number;
  total_revenue: number;
  order_count: number;
  avg_price: number;
  current_stock: number;
}

interface Summary {
  total_units_sold: number;
  total_revenue: number;
  unique_products: number;
  total_orders: number;
  avg_item_value: number;
}

export default function BestSellersReport() {
  const navigate = useNavigate();
  const { fetchApi, error, loading } = useApi();
  const [data, setData] = useState<BestSellerItem[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [exporting, setExporting] = useState(false);

  // Filters
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [sortBy, setSortBy] = useState('revenue');
  const [limit, setLimit] = useState('50');

  const loadReport = useCallback(async () => {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      sort_by: sortBy,
      limit,
    });

    const result = await fetchApi<{
      success: boolean;
      data: BestSellerItem[];
      summary: Summary;
    }>(`/reports/best-sellers?${params.toString()}`);

    if (result?.success) {
      setData(result.data || []);
      setSummary(result.summary);
    }
  }, [fetchApi, startDate, endDate, sortBy, limit]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const result = await fetchApi<{ success: boolean; download_url: string }>('/reports/best-sellers/export', {
        method: 'POST',
        body: JSON.stringify({ start_date: startDate, end_date: endDate, sort_by: sortBy }),
      });

      if (result?.success && result.download_url) {
        window.open(`${import.meta.env.VITE_API_URL}${result.download_url}`, '_blank');
      }
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = useMoney();

  const tableRows = data.map((item, index) => [
    <InlineStack gap="200" align="center" wrap={false}>
      <Badge tone={index < 3 ? 'success' : undefined}>{String(index + 1)}</Badge>
    </InlineStack>,
    <InlineStack gap="300" align="start" wrap={false}>
      {item.featured_image_url ? (
        <Thumbnail source={item.featured_image_url} alt={item.title} size="small" />
      ) : (
        <div style={{ width: 40, height: 40, background: '#f4f6f8', borderRadius: 4 }} />
      )}
      <BlockStack gap="050">
        <Button variant="plain" onClick={() => navigate(`/products/${item.product_id}`)}>
          {item.sku || '-'}
        </Button>
        <Text as="span" variant="bodySm" tone="subdued">{item.title}</Text>
      </BlockStack>
    </InlineStack>,
    item.vendor || '-',
    <Text as="span" fontWeight="bold">{String(item.total_quantity)}</Text>,
    <Text as="span" fontWeight="bold">{formatCurrency(item.total_revenue)}</Text>,
    String(item.order_count),
    formatCurrency(item.avg_price),
    <Badge tone={item.current_stock > 0 ? 'success' : 'critical'}>
      {String(item.current_stock)}
    </Badge>,
  ]);

  return (
    <Page
      title="Best Sellers Report"
      subtitle="Top performing products by sales volume and revenue"
      backAction={{ content: 'Reports', onAction: () => navigate('/reports') }}
      primaryAction={{
        content: 'Export CSV',
        onAction: handleExport,
        loading: exporting,
        disabled: data.length === 0,
      }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {/* Summary Cards */}
        {summary && (
          <Layout.Section>
            <InlineStack gap="400" wrap={false}>
              <Card>
                <BlockStack gap="200">
                  <Text as="span" variant="bodySm" tone="subdued">Total Revenue</Text>
                  <Text as="p" variant="heading2xl">{formatCurrency(summary.total_revenue)}</Text>
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text as="span" variant="bodySm" tone="subdued">Units Sold</Text>
                  <Text as="p" variant="heading2xl">{summary.total_units_sold.toLocaleString()}</Text>
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text as="span" variant="bodySm" tone="subdued">Unique Products</Text>
                  <Text as="p" variant="heading2xl">{summary.unique_products}</Text>
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text as="span" variant="bodySm" tone="subdued">Avg Item Value</Text>
                  <Text as="p" variant="heading2xl">{formatCurrency(summary.avg_item_value)}</Text>
                </BlockStack>
              </Card>
            </InlineStack>
          </Layout.Section>
        )}

        {/* Filters */}
        <Layout.Section>
          <Card>
            <InlineStack gap="400" align="start" wrap>
              <div style={{ width: '150px' }}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={setStartDate}
                  autoComplete="off"
                />
              </div>
              <div style={{ width: '150px' }}>
                <TextField
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={setEndDate}
                  autoComplete="off"
                />
              </div>
              <div style={{ width: '150px' }}>
                <Select
                  label="Sort By"
                  options={[
                    { label: 'Revenue', value: 'revenue' },
                    { label: 'Quantity', value: 'quantity' },
                  ]}
                  value={sortBy}
                  onChange={setSortBy}
                />
              </div>
              <div style={{ width: '120px' }}>
                <Select
                  label="Limit"
                  options={[
                    { label: 'Top 25', value: '25' },
                    { label: 'Top 50', value: '50' },
                    { label: 'Top 100', value: '100' },
                    { label: 'All', value: '1000' },
                  ]}
                  value={limit}
                  onChange={setLimit}
                />
              </div>
              <div style={{ paddingTop: '24px' }}>
                <Button onClick={loadReport}>Refresh</Button>
              </div>
            </InlineStack>
          </Card>
        </Layout.Section>

        {/* Data Table */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Top Products ({data.length})
              </Text>

              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                  <Spinner />
                </div>
              ) : data.length === 0 ? (
                <Banner tone="info">
                  No sales data found for the selected period.
                </Banner>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric']}
                  headings={['Rank', 'Product', 'Vendor', 'Units', 'Revenue', 'Orders', 'Avg Price', 'Stock']}
                  rows={tableRows}
                />
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
